import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { CheckCircle2, Pencil, Smartphone } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import { PodpisPad } from './PodpisPad.js';
import { Chyba, Nacitani } from './ui.js';

/**
 * Vstup vlastnoručního podpisu s volbou zařízení: kreslit tady (prst/myš),
 * NEBO „Podepsat na mobilu" — desktop ukáže QR, uživatel naskenuje vlastním
 * telefonem, podepíše prstem a podpis se doručí zpět do dialogu (handoff
 * odkaz + polling stavu). Akci pak dokončí přihlášený uživatel na desktopu.
 */
export function PodpisVstup({
  entita,
  entitaId,
  jmeno,
  onChange,
}: {
  entita: 'ZmenovyList' | 'Dodatek' | 'Zmena' | 'ZjistovaciProtokol';
  entitaId: string;
  /** Jméno podepisujícího (přihlášený uživatel) — předvyplní mobilní stránku. */
  jmeno: string;
  onChange: (dataUrl: string | null) => void;
}) {
  const [rezim, setRezim] = useState<'pad' | 'mobil'>('pad');
  const [prijaty, setPrijaty] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const qrRef = useRef<HTMLCanvasElement>(null);

  const vytvor = useMutation({
    mutationFn: () =>
      api.post<{ token: string }>('/api/podpisove-odkazy', {
        entita,
        entitaId,
        jmeno,
        handoff: true,
      }),
    onSuccess: (r) => setToken(r.token),
  });

  // Polling stavu handoffu, dokud z mobilu nedorazí podpis.
  const { data: stav } = useQuery({
    queryKey: ['podpis-handoff', token],
    queryFn: () =>
      api.get<{ podepsano: boolean; obrazek: string | null }>(
        `/api/podpisove-odkazy/${token}/stav`,
      ),
    enabled: !!token && rezim === 'mobil' && !prijaty,
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (stav?.podepsano && stav.obrazek && !prijaty) {
      setPrijaty(stav.obrazek);
      onChange(stav.obrazek);
    }
  }, [stav, prijaty, onChange]);

  useEffect(() => {
    if (token && qrRef.current && !prijaty) {
      void QRCode.toCanvas(qrRef.current, `${window.location.origin}/podpis/${token}`, {
        width: 190,
        margin: 1,
      });
    }
  }, [token, prijaty]);

  const { mutate: vytvorOdkaz } = vytvor;
  const naMobil = () => {
    setRezim('mobil');
    if (!token) vytvorOdkaz();
  };
  const naPad = () => {
    setRezim('pad');
    if (!prijaty) onChange(null);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Volba zařízení — dvě tiché záložky. */}
      <div role="radiogroup" aria-label="Kde podepsat" className="flex gap-1">
        <button
          type="button"
          role="radio"
          aria-checked={rezim === 'pad'}
          onClick={naPad}
          className={`flex min-h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors duration-100 ${
            rezim === 'pad' ? 'bg-primary-tint text-primary' : 'text-text-secondary hover:bg-bg'
          }`}
        >
          <Pencil size={15} strokeWidth={2} aria-hidden="true" /> Podepsat tady
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={rezim === 'mobil'}
          onClick={naMobil}
          className={`flex min-h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors duration-100 ${
            rezim === 'mobil' ? 'bg-primary-tint text-primary' : 'text-text-secondary hover:bg-bg'
          }`}
        >
          <Smartphone size={15} strokeWidth={2} aria-hidden="true" /> Podepsat na mobilu
        </button>
      </div>

      {rezim === 'pad' && <PodpisPad onChange={onChange} />}

      {rezim === 'mobil' && (
        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border-strong bg-white p-4">
          {vytvor.error ? <Chyba>{hlaskaZChyby(vytvor.error)}</Chyba> : null}
          {prijaty ? (
            <>
              <img src={prijaty} alt="Podpis z mobilu" className="h-20 w-auto max-w-60" />
              <p className="flex items-center gap-1.5 text-sm font-semibold text-success-text">
                <CheckCircle2 size={16} strokeWidth={2} aria-hidden="true" />
                Podpis dorazil z mobilu.
              </p>
            </>
          ) : !token && !vytvor.error ? (
            <Nacitani text="Připravuji QR…" />
          ) : (
            <>
              <canvas ref={qrRef} aria-label="QR kód pro podpis na mobilu" role="img" />
              <p className="text-center text-sm text-text-secondary">
                Naskenujte telefonem, podepište prstem — podpis se sem doručí sám.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
